/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { isJwtExpiredFromUrl } from "@/shared/utils/jwt/token-parser";
import { FieldFunctionOptions, InMemoryCache } from "@apollo/client/core";
import {
    ContentsGroupedItemsQueryVariables,
    ContentsGroupedQueryVariables,
} from "@/shared/services/graphql/generated/consumer-graph-types";

/** Anything the cursor pagination can merge - only the cursor matters. */
interface Cursored {
    cursor?: string;
}

/** Arguments of a grouped content field itself - the pagination lives on its sub fields. */
type ContentsGroupedArgs = Partial<Pick<ContentsGroupedQueryVariables, "filter" | "groupBy">>;

/**
 * Variables of the operations that read a grouped content field: the group list knows
 * `groupFirst`/`groupAfter`, the group-items query `sort`/`hitsFirst`/`hitsAfter` - so every one of them
 * is optional here.
 */
type ContentsGroupedVariables = Partial<ContentsGroupedQueryVariables & ContentsGroupedItemsQueryVariables>;

function offsetFromCursor(items: readonly { cursor?: string }[], cursor?: string) {
    // Search from the back of the list because the cursor we're
    // looking for is typically the ID of the last item.
    for (let i = items.length - 1; i >= 0; --i) {
        const item = items[i];
        if (item.cursor === cursor) {
            // Add one because the cursor identifies the item just
            // before the first item in the page we care about.
            return i + 1;
        }
    }
    // Report that the cursor could not be found.
    return -1;
}

// Custom merge function for cursor-based pagination:
// - Inserts incoming items at the correct position based on the `after` cursor
// - If the cursor is not found, either replaces the list (no `after`) or appends to the end
// - Ensures paginated data merges correctly without overwriting existing entries
function merge(existing: any, incoming: any, args: any) {
    const merged = existing?.length ? existing.slice(0) : [];
    let offset = offsetFromCursor(merged, args?.args?.after);
    // If we couldn't find the cursor, we check for after variable
    // if exists appending incoming data to the end of the list
    // else return incoming data
    if (offset < 0) {
        if (!args?.variables?.after) return incoming;
        else offset = merged.length;
    }

    for (let i = 0; i < incoming.length; ++i) {
        merged[offset + i] = incoming[i];
    }
    return merged;
}

/**
 * Cursor-based pagination merge. Unlike the legacy `merge` it only looks at the field arguments, so it
 * works no matter how the operation names its pagination variable (e.g. `$groupAfter`).
 */
export function mergePage<T extends Cursored>(
    existing: readonly T[] | undefined,
    incoming: readonly T[],
    { args }: FieldFunctionOptions<{ after?: string }>
): readonly T[] {
    if (!args?.after) return incoming;

    const merged: T[] = existing?.length ? existing.slice(0) : [];
    // Writing an already cached page again (e.g. a refetch) has to replace it in place - locating the
    // page by its `after` cursor alone would not find it and append a duplicate of every item.
    const knownOffset = incoming[0]?.cursor !== undefined ? offsetFromCursor(merged, incoming[0].cursor) : -1;

    let offset = knownOffset > 0 ? knownOffset - 1 : offsetFromCursor(merged, args.after);
    if (offset < 0) offset = merged.length;

    for (let i = 0; i < incoming.length; ++i) merged[offset + i] = incoming[i];
    return merged;
}

/**
 * Builds the cache key of a grouped content field (`contentsGrouped` and the `relatedContentsGrouped` of
 * the search context entities). Two very different requests use those fields, and they must not share a
 * cache entry:
 *
 * - **The group list** (no `hitsFirst`): keyed by filter and grouping criteria only, so every page of
 *   groups is merged into one entry and the list can grow while scrolling.
 * - **The items of a single expanded group** (`hitsFirst` is set): keyed additionally by sort and by the
 *   group/hits cursors, so each group and page gets its own entry. It cannot overwrite the group list
 *   with its partial group, and re-opening a group is a pure cache hit.
 *
 * `hitsFirst` is the marker because the group list query does not know that variable at all - the first
 * item page cannot be recognized by `hitsAfter`, which is empty there.
 */
export function contentsGroupedKeyArgs(
    args: ContentsGroupedArgs | null,
    { variables }: { variables?: ContentsGroupedVariables }
): string {
    const base = { filter: args?.filter, groupBy: args?.groupBy };

    if (variables?.hitsFirst === undefined || variables?.hitsFirst === null) return JSON.stringify(base);

    return JSON.stringify({
        ...base,
        sort: variables.sort,
        groupAfter: variables.groupAfter,
        hitsAfter: variables.hitsAfter,
    });
}

export const clientCache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                theme: {
                    keyArgs: false,
                    merge,
                },
                facetsCollection: {
                    keyArgs: false,
                    merge,
                },
                tenant: {
                    keyArgs: false,
                    merge: true,
                },
                contents: {
                    keyArgs: ["filter"],
                    merge: true,
                },
                contentsGrouped: {
                    keyArgs: contentsGroupedKeyArgs,
                    merge: true,
                },
                activities: {
                    keyArgs: ["filter"],
                    merge: true,
                },
                assets: {
                    keyArgs: ["filter"],
                    merge: true,
                },
                products: {
                    keyArgs: ["filter"],
                    merge: true,
                },
            },
        },
        AccessFilterQueryResult: {
            fields: {
                accessFilters: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        ContentMapQueryResult: {
            fields: {
                matchedFacets: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        ContentMap: {
            fields: {
                teasers: {
                    keyArgs: false,
                    merge: true,
                },
                useContext: {
                    merge: true,
                },
                variants: {
                    keyArgs: ["filter", "filterOptions"],
                    merge(existing = {}, incoming, { mergeObjects }) {
                        return mergeObjects(existing, incoming);
                    },
                },
            },
        },
        ContentQueryResult: {
            fields: {
                contents: {
                    keyArgs: ["sort"],
                    merge: mergePage,
                },
            },
        },
        ContentGroupedQueryResult: {
            fields: {
                groups: {
                    keyArgs: false,
                    merge: mergePage,
                },
            },
        },
        NoteQueryResult: {
            fields: {
                notes: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        NotificationQueryResult: {
            fields: {
                notifications: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        UserQueryResult: {
            fields: {
                users: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        WebhookConfigQueryResult: {
            fields: {
                webhookConfigs: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        TenantQueryResult: {
            fields: {
                tenants: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        MetadataDefinitionQueryResult: {
            fields: {
                metadataDefinitions: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        DateTimeRangeMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        NumberRangeMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        KeywordMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        StaticPageQueryResult: {
            fields: {
                staticPages: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        ProductQueryResult: {
            fields: {
                products: {
                    keyArgs: false,
                    merge,
                },
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        Product: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
                relatedContentsGrouped: {
                    keyArgs: contentsGroupedKeyArgs,
                    merge: true,
                },
            },
        },
        NumberMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        DateTimeMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        TaxonomyMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        TextMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        Asset: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
                relatedContentsGrouped: {
                    keyArgs: contentsGroupedKeyArgs,
                    merge: true,
                },
            },
        },
        MechanicQueryResult: {
            fields: {
                mechanics: {
                    keyArgs: false,
                    merge,
                },
                usedInProducts: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        Mechanic: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
                previewImage: {
                    merge(existing, incoming) {
                        if (!existing) return incoming;
                        // Only update if the actual blob changed (different blobId),
                        // or if the existing token is expired/about to expire
                        if (existing.blobId && incoming?.blobId && existing.blobId === incoming.blobId) {
                            // Same blob - keep existing if its token is still valid
                            if (existing.url && !isJwtExpiredFromUrl(existing.url, 30)) {
                                return existing;
                            }
                        }
                        return incoming;
                    },
                },
                teasers: {
                    keyArgs: false,
                    merge: true,
                },
                relatedContentsGrouped: {
                    keyArgs: contentsGroupedKeyArgs,
                    merge: true,
                },
            },
        },
        BlobRef: {
            keyFields: false,
        },
        Documentation: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        Article: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
                relatedContentsGrouped: {
                    keyArgs: contentsGroupedKeyArgs,
                    merge: true,
                },
            },
        },
        EnumMetadataDefinition: {
            fields: {
                localizations: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        ArticleQueryResult: {
            fields: {
                articles: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        FieldFacet: {
            fields: {
                definition: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        MetadataFacet: {
            fields: {
                definition: {
                    keyArgs: false,
                    merge: true,
                },
            },
        },
        Topic: {
            fields: {
                teasers: {
                    keyArgs: false,
                    merge: true,
                },
                useContext: {
                    merge: true,
                },
            },
        },
        TocResult: {
            fields: {
                tocEntries: {
                    keyArgs: ["path"],
                    merge,
                },
            },
        },
        AssetQueryResult: {
            fields: {
                assets: {
                    keyArgs: ["id"],
                    merge,
                },
            },
        },
        CollectionEntryQueryResult: {
            fields: {
                entries: {
                    keyArgs: ["cursor"],
                    merge,
                },
            },
        },
        CollectionQueryResult: {
            fields: {
                collections: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        ActivityQueryResult: {
            fields: {
                activities: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        StructQueryResult: {
            fields: {
                structs: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        ItemQueryResult: {
            fields: {
                items: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        PartQueryResult: {
            fields: {
                parts: {
                    keyArgs: false,
                    merge,
                },
            },
        },
        GuiElement: {
            // keyFields: false disables normalization so that each guiElements(id, type) query
            // stores its own inline copy of elements. Without this, GuiElements with the same id
            // but different types (buttons vs cards) overwrite each other in the shared entity cache,
            // causing stale/wrong data to be returned when navigating between views.
            keyFields: false,
        },
    },
});
